import logger from '../helpers/logger';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../store/slices/authSlice';
import RegistrationForm from '../components/forms/RegistrationForm';
import callApi from "../helpers/networkHelper";
import { handleJwt } from "../helpers/handleJwt";
import API_CONFIG from '../config/apiConstant';
import useAlert from '../components/hook/useAlert';


const Registration = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const { showError, showSuccess } = useAlert();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleRegistration = async (formData) => {
        setIsLoading(true);
        setError('');

        try {
            const method = API_CONFIG.AUTH.REGISTER.METHOD;
            const url = API_CONFIG.AUTH.REGISTER.ENDPOINT;
            const response = await callApi(method, url, formData);

            // Extract user data from response
            const userData = response.data.data || response.data.user || response.data;

            // Handle JWT token storage using helper
            if (userData.token || userData.accessToken) {
                const tokenData = {
                    ...userData,
                    token: userData.token || userData.accessToken,
                    loggedIn: true
                };
                handleJwt(tokenData);
            }

            // Dispatch to Redux store
            dispatch(loginSuccess({
                user: userData,
                token: userData.token || userData.accessToken,
            }));

            showSuccess("Account created successfully!");

            setSuccess(true);
            // Auto-navigate after successful registration
            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);
        } catch (error) {
            logger.info("API error: Registration failed", error);
            const errMessage = error?.response?.data?.message || error?.response?.data?.error || "Registration request failed!";
            // setError(errMessage);
            showError(errMessage);

        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-background text-foreground flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors duration-300">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-card py-8 px-4 shadow-card border border-border sm:rounded-lg sm:px-10">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-success/15">
                                <svg className="h-6 w-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h3 className="mt-4 text-lg font-medium text-foreground">Account created successfully!</h3>
                            <p className="mt-2 text-sm text-muted-foreground">
                                You're being redirected to the dashboard...
                            </p>
                            <div className="mt-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-success mx-auto"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors duration-300">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="flex justify-center">
                    <div className="bg-brand rounded-full p-3 shadow-card">
                        <svg className="h-8 w-8 text-on-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                    </div>
                </div>
                <h2 className="mt-6 text-center text-3xl font-bold text-foreground">
                    Create your account
                </h2>
                <p className="mt-2 text-center text-md font-medium text-muted-foreground">
                    Join us to manage your devices efficiently
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-card py-8 px-4 shadow-card border border-border sm:rounded-lg sm:px-10">
                    {error && (
                        <div className="mb-4 bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg">
                            <div className="flex">
                                <svg className="h-5 w-5 text-destructive mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                {error}
                            </div>
                        </div>
                    )}

                    <RegistrationForm onSubmit={handleRegistration} isLoading={isLoading} />

                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-border" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-card text-muted-foreground">Already have an account?</span>
                            </div>
                        </div>

                        <div className="mt-6">
                            <Link
                                to="/login"
                                className="w-full flex justify-center py-2 px-4 border border-brand rounded-lg shadow-sm bg-card text-sm font-medium text-brand hover:bg-brand-light duration-200"
                            >
                                Sign in to existing account
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Registration;
